import { Dtos } from '#models';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  forwardRef,
  inject,
  input,
  TemplateRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { InputText } from 'primeng/inputtext';
import { distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'app-external-user-form',
  imports: [ReactiveFormsModule, InputText, TranslatePipe, CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ExternalUserForm),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ExternalUserForm),
      multi: true,
    },
  ],
  templateUrl: './external-user-form.html',
  styleUrl: './external-user-form.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExternalUserForm implements ControlValueAccessor, Validator {
  private _destroyRef = inject(DestroyRef);
  private id = 0;

  isDisabled = input(false);
  isReadOnly = input(false);
  actionTemplate = input<TemplateRef<unknown> | null>(null);

  formGroup = new FormGroup({
    email: new FormControl<string>('', {
      validators: [Validators.required, Validators.email],
    }),
    firstName: new FormControl<string>('', {
      validators: [Validators.required],
    }),
    lastName: new FormControl<string>(''),
  });

  constructor() {
    effect(() => {
      const disabled = this.isDisabled();
      ['firstName', 'lastName', 'email'].forEach(name => {
        const ctrl = this.formGroup.get(name);
        if (!ctrl) {
          return;
        }
        const action = disabled ? 'disable' : 'enable';
        ctrl?.[action]({ emitEvent: false });
      });
    });
  }

  validate = (): ValidationErrors | null => {
    let errors = {};
    const control = this.formGroup;
    if (control.get('firstName')?.invalid) {
      errors = { ...control.get('firstName')?.errors };
    }
    if (control.get('email')?.invalid) {
      errors = { ...errors, ...control.get('email')?.errors };
    }
    const result = Object.values(errors).length > 0 ? errors : null;
    return result;
  };

  writeValue(obj: Dtos.User): void {
    this.id = obj?.ID || 0;

    this.formGroup.patchValue(
      {
        email: obj.Email ?? '',
        firstName: obj.FirstName ?? '',
        lastName: obj.LastName ?? '',
      },
      { emitEvent: false }
    );

    this.formGroup.updateValueAndValidity({ emitEvent: false });
    this.formGroup.markAsUntouched();
    this.formGroup.markAsPristine();
  }
  registerOnChange(fn: (value: Dtos.User) => void): void {
    this.formGroup.valueChanges.pipe(distinctUntilChanged(), takeUntilDestroyed(this._destroyRef)).subscribe(value => {
      fn({
        ID: this.id,
        Email: value.email ?? '',
        FirstName: value.firstName ?? '',
        LastName: value.lastName ?? '',
      } as Dtos.User);
    });
  }

  registerOnTouched(fn: () => void): void {
    this.formGroup.statusChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      if (this.formGroup.touched) {
        fn();
      }
    });
  }

  isInvalidEmail() {
    const email = this.formGroup.get('email');
    if (!email) {
      return false;
    }
    return email.invalid && (email.touched || email.dirty);
  }

  isInvalidFirstName() {
    const firstName = this.formGroup.get('firstName');
    if (!firstName) {
      return false;
    }
    return firstName.invalid && (firstName.touched || firstName.dirty);
  }
}
